import { useState } from "react";
import { IconAlertTriangle, IconCircleCheck, IconDeviceFloppy, IconFileText, IconX } from "@tabler/icons-react";
import {
  Alert,
  Badge,
  Button,
  Card,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  Group,
  Stack,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
} from "@/components/ui";
import { formatearMonto } from "@/shared/format/money";
import { etiquetaEstatusCompra } from "@/shared/format/estatus-compra";
import type { ContratoAnualizadoAdmin } from "@/lib/types";
import {
  AltaCatalogoModal,
  CeldaCatalogo,
  useCatalogoVerificacion,
  type TipoCatalogo,
} from "@/features/admin-compras-shared/catalogo-verificacion";

function Campo({ label, valor }: { label: string; valor: React.ReactNode }) {
  return (
    <Group gap={6} className="flex-nowrap items-baseline">
      <Text size="xs" c="dimmed" className="min-w-22">{label}</Text>
      <Text size="sm" fw={500}>{valor}</Text>
    </Group>
  );
}

function TituloSeccion({ children }: { children: React.ReactNode }) {
  return (
    <Text size="xs" fw={700} c="multiva" className="uppercase tracking-wide">
      {children}
    </Text>
  );
}

/** Previsualización del contrato anualizado parseado desde un XML, ANTES de confirmar la carga. */
export function ContratoPreviewModal({
  contrato,
  confirmando,
  onConfirm,
  onCancel,
}: {
  contrato: ContratoAnualizadoAdmin | null;
  confirmando: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  const { cargando, estadoCuenta, estadoCeco } = useCatalogoVerificacion();
  const [alta, setAlta] = useState<{ tipo: TipoCatalogo; codigo: string } | null>(null);

  const faltantes = (contrato?.conceptos ?? []).filter(
    (c) => estadoCuenta(c.cuentaContable) === "no" || estadoCeco(c.centroCostos) === "no"
  ).length;

  return (
    <Dialog open={contrato !== null} onOpenChange={(open) => !open && onCancel()}>
      <DialogContent className="max-w-4xl">
        <DialogHeader>
          <DialogTitle>
            <Group gap={8}>
              <IconFileText size={18} />
              <span>Previsualización del contrato a cargar</span>
              <Badge variant="light" color="advertencia" size="sm">Sin guardar</Badge>
            </Group>
          </DialogTitle>
        </DialogHeader>
        {contrato && (
          <Stack gap="md">
            {!cargando && faltantes > 0 && (
              <Alert variant="error" icon={<IconAlertTriangle size={18} />} title="Catálogos incompletos">
                {faltantes} posición(es) tienen cuenta contable o centro de costos que{" "}
                <strong>no existen</strong> en los catálogos. Usa el botón verde (+) para darlos de alta
                antes de cargar.
              </Alert>
            )}
            {!cargando && faltantes === 0 && contrato.conceptos.length > 0 && (
              <Alert variant="success" icon={<IconCircleCheck size={18} />} title="Catálogos verificados">
                Todas las cuentas contables y centros de costo de las posiciones existen en los catálogos.
              </Alert>
            )}

            <Card padding="md">
              <Stack gap="sm">
                <TituloSeccion>Datos de cabecera</TituloSeccion>
                <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                  <Campo label="No. Contrato" valor={<span className="font-mono">{contrato.clave}</span>} />
                  <Campo label="Fecha" valor={(contrato.fecha ?? "").slice(0, 10) || "—"} />
                  <Campo label="Proveedor" valor={contrato.proveedor.nombre} />
                  <Campo label="RFC" valor={<span className="font-mono">{contrato.proveedor.rfc}</span>} />
                  <Campo label="Moneda" valor={contrato.moneda} />
                  <Campo label="Estatus" valor={etiquetaEstatusCompra(contrato.estatus)} />
                </div>
              </Stack>
            </Card>

            <Card padding="md">
              <Stack gap="sm">
                <TituloSeccion>Detalle por posición ({contrato.conceptos.length})</TituloSeccion>
                <div className="overflow-x-auto">
                  <Table verticalSpacing="xs" withRowBorders>
                    <Thead>
                      <Tr>
                        <Th>Línea</Th>
                        <Th>Descripción</Th>
                        <Th align="right">Cantidad</Th>
                        <Th>Unidad</Th>
                        <Th align="right">V. unitario</Th>
                        <Th align="right">Importe</Th>
                        <Th>Cuenta</Th>
                        <Th>CeCo</Th>
                      </Tr>
                    </Thead>
                    <Tbody>
                      {contrato.conceptos.map((c, i) => (
                        <Tr key={i}>
                          <Td className="font-mono">{c.lineaPedido}</Td>
                          <Td>{c.descripcion}</Td>
                          <Td align="right">{c.cantidad}</Td>
                          <Td>{c.unidad ?? "—"}</Td>
                          <Td align="right">{formatearMonto(c.valorUnitario, contrato.moneda)}</Td>
                          <Td align="right">{formatearMonto(c.importe, contrato.moneda)}</Td>
                          <Td>
                            <CeldaCatalogo
                              valor={c.cuentaContable}
                              estado={estadoCuenta(c.cuentaContable)}
                              onAgregar={c.cuentaContable ? () => setAlta({ tipo: "cuenta", codigo: c.cuentaContable! }) : undefined}
                            />
                          </Td>
                          <Td>
                            <CeldaCatalogo
                              valor={c.centroCostos}
                              estado={estadoCeco(c.centroCostos)}
                              onAgregar={c.centroCostos ? () => setAlta({ tipo: "ceco", codigo: c.centroCostos! }) : undefined}
                            />
                          </Td>
                        </Tr>
                      ))}
                    </Tbody>
                  </Table>
                </div>
              </Stack>
            </Card>

            <Card padding="md">
              <Group justify="space-between">
                <TituloSeccion>Importe del contrato</TituloSeccion>
                <Group gap={8} align="baseline" className="flex-nowrap">
                  <Text fw={700} className="text-[28px] leading-none">{formatearMonto(contrato.total, contrato.moneda)}</Text>
                  <Text fw={700} c="multiva" className="text-[18px] leading-none">{contrato.moneda?.toUpperCase()}</Text>
                </Group>
              </Group>
            </Card>

            <Group justify="flex-end">
              <Button variant="default" leftSection={<IconX size={14} />} onClick={onCancel}>
                Cancelar
              </Button>
              <Button leftSection={<IconDeviceFloppy size={14} />} loading={confirmando} onClick={onConfirm}>
                Confirmar carga
              </Button>
            </Group>
          </Stack>
        )}

        <AltaCatalogoModal
          tipo={alta?.tipo ?? null}
          codigoInicial={alta?.codigo ?? ""}
          onClose={() => setAlta(null)}
        />
      </DialogContent>
    </Dialog>
  );
}
